/* Email domain typo suggestions ("did you mean gmail.com?"). Shared across the
   global-tickets and responsive template trees, since checkout/login/password-reset
   pages render on the responsive template while contact/VIP/groups/earlybird render
   on global-tickets. Loaded via a <script> tag from both templates' general.js.php. */
$(function() {
  // Set from the "email_domain_suggestion" language constant by each template's
  // general.js.php (window.GT_EMAIL_SUGGESTION_PREFIX).
  const suggestionPrefixText = window.GT_EMAIL_SUGGESTION_PREFIX;

  // French puts a space before "?".
  const languageCode = window.GT_LANGUAGE_CODE;
  const suggestionTrailingMark = languageCode === 'fr' ? ' ?' : '?';

  // The 7 domains covering ~85% of global email client market share, with all
  // regionally valid TLD variants. A domain outside this list is left alone.
  const KNOWN_EMAIL_DOMAINS = {
    gmail: ['gmail.com'],
    yahoo: [
      'yahoo.com', 'yahoo.co.uk', 'yahoo.co.jp', 'yahoo.fr', 'yahoo.de', 'yahoo.es',
      'yahoo.it', 'yahoo.com.ar', 'yahoo.com.br', 'yahoo.com.mx', 'yahoo.com.au',
      'yahoo.com.sg', 'yahoo.com.ph', 'yahoo.gr', 'yahoo.ro', 'yahoo.dk', 'yahoo.se',
      'yahoo.no', 'yahoo.ie',
    ],
    hotmail: [
      'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it',
      'hotmail.es', 'hotmail.com.ar', 'hotmail.com.br', 'hotmail.com.mx', 'hotmail.be',
      'hotmail.nl', 'hotmail.se', 'hotmail.no', 'hotmail.dk', 'hotmail.ch', 'hotmail.at',
      'hotmail.pt', 'hotmail.gr',
    ],
    aol: ['aol.com'],
    outlook: ['outlook.com', 'outlook.be', 'outlook.com.ar', 'outlook.com.br', 'outlook.com.mx'],
    icloud: ['icloud.com'],
    me: ['me.com'],
  };

  // Max Levenshtein distance still considered "a typo of this provider name",
  // scaled by name length so short names (aol, me) don't over-match unrelated domains.
  function maxDistanceForRoot(domainRoot) {
    if (domainRoot.length <= 3) return 0;
    if (domainRoot.length <= 5) return 1;
    return 2;
  }

  function levenshtein(wordA, wordB) {
    const lengthA = wordA.length, lengthB = wordB.length;
    if (lengthA === 0) return lengthB;
    if (lengthB === 0) return lengthA;

    const distanceRow = new Array(lengthB + 1);
    for (let col = 0; col <= lengthB; col++) distanceRow[col] = col;

    for (let row = 1; row <= lengthA; row++) {
      let diagonalDistance = distanceRow[0];
      distanceRow[0] = row;
      for (let col = 1; col <= lengthB; col++) {
        const previousRowDistance = distanceRow[col];
        const substitutionCost = wordA[row - 1] === wordB[col - 1] ? 0 : 1;
        distanceRow[col] = Math.min(
          distanceRow[col] + 1,       // deletion
          distanceRow[col - 1] + 1,   // insertion
          diagonalDistance + substitutionCost // substitution
        );
        diagonalDistance = previousRowDistance;
      }
    }
    return distanceRow[lengthB];
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isCompleteEmail(email) {
    return EMAIL_RE.test($.trim(email || ''));
  }

  // Returns null if no correction should be suggested, otherwise { email, domain }.
  function getEmailDomainSuggestion(email) {
    if (typeof email !== 'string') return null;
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) return null;

    const localPart = email.slice(0, atIndex);
    const rawDomain = email.slice(atIndex + 1).toLowerCase();
    if (!localPart || !rawDomain) return null;

    // Remove consecutive dots and strip a trailing dot.
    const normalizedDomain = rawDomain.replace(/\.{2,}/g, '.').replace(/\.+$/, '');
    const domainRoot = normalizedDomain.split('.')[0];
    if (!domainRoot) return null;

    let closestProviderName = null;
    let closestProviderDistance = Infinity;
    Object.keys(KNOWN_EMAIL_DOMAINS).forEach(function(providerName) {
      const distance = domainRoot === providerName ? 0 : levenshtein(domainRoot, providerName);
      if (distance < closestProviderDistance) {
        closestProviderDistance = distance;
        closestProviderName = providerName;
      }
    });
    if (closestProviderName === null || closestProviderDistance > maxDistanceForRoot(closestProviderName)) {
      return null; // Not close enough to any of the 7 recognised domains.
    }

    const validDomainsForProvider = KNOWN_EMAIL_DOMAINS[closestProviderName];
    const isRecognisedVariant = validDomainsForProvider.indexOf(normalizedDomain) !== -1;
    const hadDotIssue = normalizedDomain !== rawDomain;

    if (isRecognisedVariant && !hadDotIssue) {
      return null; // Already correct, including legitimate regional variants.
    }

    const correctedDomain = isRecognisedVariant
      ? normalizedDomain // Only a double-dot/trailing-dot fix was needed; keep the valid regional TLD.
      : closestProviderName + '.com'; // Unrecognised root spelling and/or TLD -> fall back to the canonical .com.

    if (correctedDomain === rawDomain) return null;

    return { domain: correctedDomain, email: localPart + '@' + correctedDomain };
  }

  // Wires a single email input up to show/accept "did you mean ...?" suggestions.
  // Nothing is shown when the address is already correct.
  function attachEmailDomainSuggestion(inputOrSelector) {
    const emailInput = inputOrSelector.jquery ? inputOrSelector : $(inputOrSelector);
    if (!emailInput.length || emailInput.data('gtEmailSuggestionAttached')) return;
    emailInput.data('gtEmailSuggestionAttached', true);

    const formContainer = emailInput.closest('.form-container');
    const hintElement = $('<div class="gt-email-domain-hint" aria-live="polite"></div>');
    if (formContainer.length) {
      // Show hint under the input field focus underline.
      hintElement.appendTo(formContainer);
    } else {
      hintElement.insertAfter(emailInput);
    }
    let pendingSuggestion = null;

    // The checkout forms show their own green "form-success" tick (see checkout.js) purely
    // based on email format validity. When we're suggesting a likely-typo domain, that address
    // still passes format validation, so we have to explicitly suppress it to avoid contradicting
    // our own suggestion.
    function suppressSuccessState() {
      if (!formContainer.length) return;
      formContainer.removeClass('form-success');
      formContainer.find('.form-success-icon').hide();
    }

    function restoreSuccessState() {
      if (!formContainer.length) return;
      // Clear our forced inline hide and let checkout.js's own form-success class drive it again.
      formContainer.find('.form-success-icon').css('display', '');
    }

    function clearHint() {
      pendingSuggestion = null;
      hintElement.empty().removeClass('has-suggestion');
      restoreSuccessState();
    }

    function showSuggestion(suggestion) {
      pendingSuggestion = suggestion;
      hintElement.empty().addClass('has-suggestion');
      $('<span class="gt-email-domain-suggestion-text"></span>').text(suggestionPrefixText + " ").appendTo(hintElement);
      $('<button type="button" class="gt-email-domain-suggestion-link"></button>')
        .text(suggestion.email)
        .appendTo(hintElement);
      $('<span class="gt-email-domain-suggestion-text"></span>').text(suggestionTrailingMark).appendTo(hintElement);
      setTimeout(suppressSuccessState, 0);
    }

    function runCheck() {
      const emailValue = $.trim(emailInput.val());
      if (!isCompleteEmail(emailValue)) {
        clearHint();
        return;
      }

      const suggestion = getEmailDomainSuggestion(emailValue);
      if (suggestion) {
        showSuggestion(suggestion);
      } else {
        clearHint();
      }
    }

    // Use mousedown/touchstart (not click) and preventDefault so the button never steals
    // focus from the input. Otherwise the resulting blur fires runCheck() first, which
    // rebuilds the hint - destroying this very button mid-click/tap - and the click never
    // lands, requiring a second click/tap once the input has already blurred. touchstart is
    // bound alongside mousedown for mobile: browsers normally
    // synthesize mousedown from a tap, but if that ever doesn't happen the explicit
    // touchstart handler still fires. If both fire for the same tap, the second call is a
    // harmless no-op since pendingSuggestion will already be null by then.
    hintElement.on('mousedown touchstart', '.gt-email-domain-suggestion-link', function(e) {
      e.preventDefault();
      if (!pendingSuggestion) return;
      emailInput.val(pendingSuggestion.email).trigger('input').trigger('change');
      runCheck();
    });

    emailInput.on('blur', runCheck);

    emailInput.on('input', function() {
      const emailValue = $.trim($(this).val());
      if (isCompleteEmail(emailValue)) {
        runCheck();
      } else {
        clearHint();
      }
    });
  }

  // Fields covered by the "did you mean" domain-typo check, across checkout,
  // exclusive experiences, password reset, earlybird and contact forms.
  [
    '#input-login_email_address',   // Checkout: login
    '#input-email_address_1',       // Checkout: signup (new account)
    '#input-email_address_0',       // Checkout: guest checkout
    '#input-email',                 // Password reset request
    '#eb-basic-email',              // Earlybird signup
    '#contact_email',               // Exclusive experiences: VIP / Groups
    '#mail',                        // Contact form
  ].forEach(function(fieldSelector) {
    const emailField = $(fieldSelector);

    if (emailField.length) {
      attachEmailDomainSuggestion(emailField);
    }

  });
});
